/**
 * Transporte do oplog sobre o Google Drive: cada aparelho sobe segmentos APPEND-ONLY nos próprios
 * arquivos `kontto_oplog_<deviceId>_<seq>.kseg` (cifrados — ver wire.ts) e baixa os dos peers.
 * Reusa o driveClient/pasta do backup (ADR 0027, escopo `drive.file`). Ver docs/features/SYNC.md § 3.
 *
 * Fino de propósito: só nomeação + I/O. A serialização mora em wire.ts; a orquestração em sync.ts.
 */

import { deleteBackup, downloadBackup, listByPrefix, uploadBackup, type DriveFile } from '@/lib/googleDrive/driveClient';
import { DEVICE_FILE_PREFIX, deviceFileContent, deviceFileName, parseDeviceFileName, type DeviceCapabilities } from './capabilities';

const OPLOG_PREFIX = 'kontto_oplog_';
const OPLOG_EXT = '.kseg';
/** Tudo que o sync e o backup deixam na pasta começa assim — uma listagem serve pra tudo. */
const SPACE_PREFIX = 'kontto_';
/** O meta do espaço (salt + verificador da chave — ver syncKey.ts). */
const META_NAME = 'kontto_sync_meta.json';

export interface RemoteSegment {
    file: DriveFile;
    deviceId: string;
    seq: number;
}

export function segmentName(deviceId: string, seq: number): string {
    return `${OPLOG_PREFIX}${deviceId}_${seq}${OPLOG_EXT}`;
}

/** Extrai (deviceId, seq) do nome, ou null se não for um segmento. deviceId é hex (sem '_'). */
export function parseSegmentName(name: string): { deviceId: string; seq: number } | null {
    if (!name.startsWith(OPLOG_PREFIX) || !name.endsWith(OPLOG_EXT)) return null;
    const core = name.slice(OPLOG_PREFIX.length, -OPLOG_EXT.length);
    const cut = core.lastIndexOf('_');
    if (cut <= 0) return null;
    const deviceId = core.slice(0, cut);
    const seq = Number(core.slice(cut + 1));
    if (!deviceId || !Number.isInteger(seq)) return null;
    return { deviceId, seq };
}

/** Sobe um segmento (envelope cifrado) do próprio aparelho; devolve o arquivo como o Drive o listaria. */
export async function uploadSegment(
    token: string,
    folderId: string,
    deviceId: string,
    seq: number,
    envelope: string,
): Promise<RemoteSegment> {
    const file = await uploadBackup(token, folderId, segmentName(deviceId, seq), envelope);
    return { file, deviceId, seq };
}

function toSegments(files: DriveFile[]): RemoteSegment[] {
    const segments: RemoteSegment[] = [];
    for (const file of files) {
        const parsed = parseSegmentName(file.name);
        if (parsed) segments.push({ file, deviceId: parsed.deviceId, seq: parsed.seq });
    }
    return segments.sort((a, b) => (a.deviceId === b.deviceId ? a.seq - b.seq : a.deviceId < b.deviceId ? -1 : 1));
}

/** Lista todos os segmentos de oplog na pasta (de todos os aparelhos), em ordem de (device, seq). */
export async function listSegments(token: string, folderId: string): Promise<RemoteSegment[]> {
    return toSegments(await listByPrefix(token, folderId, OPLOG_PREFIX));
}

/** Um arquivo de capacidade (ADR 0181) como está na pasta: o que o nome diz + o arquivo. */
export interface RemoteDeviceFile {
    file: DriveFile;
    caps: DeviceCapabilities;
}

/**
 * O espaço inteiro numa listagem só (o driveClient lista a pasta e filtra por prefixo — duas
 * listagens custariam duas idas ao Drive pelo mesmo resultado): segmentos em ordem de
 * (device, seq) e os arquivos de capacidade dos aparelhos.
 */
export async function listSpace(
    token: string,
    folderId: string,
): Promise<{ segments: RemoteSegment[]; devices: RemoteDeviceFile[]; metas: DriveFile[] }> {
    const files = await listByPrefix(token, folderId, SPACE_PREFIX);
    const devices: RemoteDeviceFile[] = [];
    for (const file of files) {
        if (!file.name.startsWith(DEVICE_FILE_PREFIX)) continue;
        const caps = parseDeviceFileName(file.name);
        if (caps) devices.push({ file, caps });
    }
    return { segments: toSegments(files), devices, metas: files.filter((f) => f.name === META_NAME) };
}

/**
 * Os arquivos do SYNC na pasta: segmentos, arquivos de capacidade e o meta. É o que redefinir a
 * sincronização apaga (ADR 0191). Filtra pelos prefixos PRÓPRIOS do sync, nunca só por `kontto_`:
 * os pontos de restauração (`kontto_backup_*`) moram na mesma pasta e não são do sync.
 */
export async function listSyncFiles(token: string, folderId: string): Promise<{ metas: DriveFile[]; others: DriveFile[] }> {
    const files = await listByPrefix(token, folderId, SPACE_PREFIX);
    const metas = files.filter((f) => f.name === META_NAME);
    const others = files.filter((f) => f.name.startsWith(OPLOG_PREFIX) || f.name.startsWith(DEVICE_FILE_PREFIX));
    return { metas, others };
}

/** Apaga um arquivo do sync (redefinição — ADR 0191). */
export async function deleteSyncFile(token: string, fileId: string): Promise<void> {
    await deleteBackup(token, fileId);
}

/** Publica a capacidade deste aparelho (um arquivo cujo NOME é a capacidade — ver capabilities.ts). */
export async function uploadDeviceFile(token: string, folderId: string, caps: DeviceCapabilities, nowIso: string): Promise<void> {
    await uploadBackup(token, folderId, deviceFileName(caps), deviceFileContent(caps, nowIso));
}

/** Apaga um arquivo de capacidade antigo deste aparelho (nome com capacidade superada). */
export async function deleteDeviceFile(token: string, fileId: string): Promise<void> {
    await deleteBackup(token, fileId);
}

/** Baixa o envelope cru de um segmento (pra decifrar/aplicar). */
export async function downloadSegment(token: string, fileId: string): Promise<string> {
    return downloadBackup(token, fileId);
}

/** Apaga um segmento (GC — ADR 0040). Só chamar para arquivo COBERTO por um checkpoint. */
export async function deleteSegment(token: string, fileId: string): Promise<void> {
    await deleteBackup(token, fileId);
}
